/**
 * AssistantSettings.tsx — Proactive Assistant settings (ProfilePage "Assistant" tab).
 *
 * Panels: master enable toggle → schedule (timezone + morning/evening briefing
 * slots) → channel cards → urgent alerts + quiet hours.
 *
 * Every failure surfaces via react-hot-toast. Do not copy the silent catch
 * blocks in EmailDigestSettings — that component predates the toast rule.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Globe, Sunrise, Moon, Mail, TriangleAlert, Eye, X, Calendar } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../packages/api-client';
import {
    getAssistantPrefs,
    updateAssistantPrefs,
    listAssistantChannels,
    updateAssistantChannel,
    deleteAssistantChannel,
    linkTelegram,
    previewBriefing,
    type AssistantPrefs,
    type AssistantChannel,
    type TelegramLinkResponse,
    type BriefingPreview,
} from '../packages/api-client/assistant-api';
import {
    listIntegrations,
    getGoogleConsentURL,
    connectICloud,
    deleteIntegration,
    type Integration,
} from '../packages/api-client/integrations-api';

// Telegram link flow: after minting a code we poll the channel list until the
// bot webhook marks the channel verified, then stop. Bounded so an abandoned
// dialog doesn't poll forever (the code itself expires in 15 minutes server-side).
const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 120_000;

// ─── Timezone helpers ─────────────────────────────────────────────────────────

const FALLBACK_TIMEZONES = [
    'UTC', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'America/Chicago',
    'America/Los_Angeles', 'America/New_York', 'Asia/Dubai', 'Asia/Kolkata',
    'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Europe/Berlin',
    'Europe/London', 'Europe/Paris',
];

function browserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

function timezoneOptions(current: string): string[] {
    // Intl.supportedValuesOf is missing from this tsconfig's ES2020 lib and
    // from older browsers, so feature-detect and fall back to a curated list.
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    let zones = FALLBACK_TIMEZONES;
    if (typeof intl.supportedValuesOf === 'function') {
        try {
            zones = intl.supportedValuesOf('timeZone');
        } catch { /* keep the fallback list */ }
    }
    // A pref saved under a zone this browser doesn't enumerate (legacy alias
    // like Asia/Calcutta) must still render as the selected <option>.
    return current && !zones.includes(current) ? [current, ...zones] : zones;
}

// ─── Shared card chrome ───────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3)',
    background: 'var(--color-surface-2)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * A single "HH:MM" briefing/quiet-hours slot. Local state stores null for a
 * cleared slot (mirroring the GET shape); the PATCH boundary in handleSave
 * serializes null → "" because the wire contract (Task 4 applyTimeField,
 * FIXED) is: absent = unchanged, "" = clear to NULL, "HH:MM" = set. Never
 * send null over the wire.
 */
function TimeField({ id, label, icon: Icon, value, onChange }: {
    id: string;
    label: string;
    icon: LucideIcon;
    value: string | null;
    onChange: (v: string | null) => void;
}) {
    // The clear button's accessible name deliberately does NOT reuse `label`
    // verbatim (e.g. "Clear morning briefing"): RTL's getByLabelText matches
    // aria-label substrings on any element, not just the <label>-associated
    // input, so a name containing the full label text collides with
    // getByLabelText(/morning briefing/i) and throws "multiple elements".
    // Derive a short, still-descriptive name from the field id instead.
    const shortName = id.replace(/^assistant-/, '').replace(/-/g, ' ');
    return (
        <div style={{ flex: 1, minWidth: 180 }}>
            <label className="form-label" htmlFor={id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                <Icon size={14} aria-hidden="true" /> {label}
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
                <input
                    id={id}
                    type="time"
                    className="form-input"
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
                />
                {value !== null && (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        aria-label={`Clear ${shortName}`}
                        onClick={() => onChange(null)}
                    >
                        <X size={14} aria-hidden="true" />
                    </button>
                )}
            </div>
        </div>
    );
}

function ChannelCard({ channel, icon: Icon, title, onToggle, onDelete }: {
    channel: AssistantChannel;
    icon: LucideIcon;
    title: string;
    onToggle: (ch: AssistantChannel, field: 'receive_briefings' | 'receive_urgent', value: boolean) => void;
    onDelete: (ch: AssistantChannel) => void;
}) {
    return (
        <div style={{ ...cardStyle, alignItems: 'flex-start' }}>
            <Icon size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{title}</span>
                    <span className="badge badge-success">Connected</span>
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {channel.address_masked}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={channel.receive_briefings}
                            onChange={e => onToggle(channel, 'receive_briefings', e.target.checked)}
                        />
                        Briefings
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={channel.receive_urgent}
                            onChange={e => onToggle(channel, 'receive_urgent', e.target.checked)}
                        />
                        Urgent alerts
                    </label>
                </div>
            </div>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(channel)}>
                Disconnect
            </button>
        </div>
    );
}

// ─── Calendar integrations ────────────────────────────────────────────────────

/**
 * Human copy for the ?calendar_error=<reason> codes the OAuth callback
 * redirects with (backend integrations/handler.go redirectErr). Anything
 * unrecognised falls back to a generic message rather than rendering a raw
 * machine code at the user.
 */
const CALENDAR_ERRORS: Record<string, string> = {
    denied: 'You declined access, so Google Calendar was not connected.',
    invalid: 'That connection link expired. Click Connect to try again.',
    exchange: 'Google rejected the connection. Click Connect to try again.',
    no_refresh_token: 'Google did not return a durable token. Disconnect the app in your Google account settings, then connect again.',
    unconfigured: 'Google Calendar is not configured on this server.',
    store: 'We could not save the connection. Try again in a moment.',
};

/** Human name per provider. */
const PROVIDER_NAMES: Record<string, string> = {
    google_calendar: 'Google Calendar',
    icloud_caldav: 'iCloud Calendar',
};

/** Apple's own instructions for generating an app-specific password. */
const APPLE_APP_PASSWORD_DOCS = 'https://support.apple.com/en-us/102654';

function IntegrationCard({ integration, onDisconnect, onReconnect }: {
    integration: Integration;
    onDisconnect: (i: Integration) => void;
    onReconnect: (i: Integration) => void;
}) {
    const name = PROVIDER_NAMES[integration.provider] ?? integration.provider;
    const broken = integration.status === 'error';

    return (
        <div style={{ ...cardStyle, alignItems: 'flex-start' }}>
            <Calendar size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{name}</span>
                    {broken
                        ? <span className="badge badge-danger">Needs attention</span>
                        : <span className="badge badge-success">Connected</span>}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {integration.account_label}
                </div>

                {/* The three-strikes lock made visible. Without this the user's
                    calendar would silently stop updating and their briefings
                    would quietly go stale with no way to know why. */}
                {broken && integration.last_error && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--space-1)' }}>
                        {integration.last_error}
                    </div>
                )}

                {integration.last_sync_at && !broken && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                        Last synced {new Date(integration.last_sync_at).toLocaleString()}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                {broken && (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        aria-label={`Reconnect ${name}`}
                        onClick={() => onReconnect(integration)}
                    >
                        Reconnect
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    aria-label={`Disconnect ${name}`}
                    onClick={() => onDisconnect(integration)}
                >
                    Disconnect
                </button>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AssistantSettings() {
    const [prefs, setPrefs] = useState<AssistantPrefs | null>(null);
    const [channels, setChannels] = useState<AssistantChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [togglingEnabled, setTogglingEnabled] = useState(false);
    const [tgLink, setTgLink] = useState<TelegramLinkResponse | null>(null);
    const [linking, setLinking] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollDeadlineRef = useRef(0);
    const [preview, setPreview] = useState<BriefingPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [integrations, setIntegrations] = useState<Integration[]>([]);
    const [connectingGoogle, setConnectingGoogle] = useState(false);
    const [appleId, setAppleId] = useState('');
    const [appPassword, setAppPassword] = useState('');
    const [connectingICloud, setConnectingICloud] = useState(false);

    useEffect(() => {
        Promise.all([
            getAssistantPrefs(),
            listAssistantChannels(),
            // CALENDAR FAILURES DEGRADE, THEY NEVER BREAK — the same rule the
            // backend applies to briefings (a calendar sync failure costs a
            // briefing its calendar section, never the briefing). In the same
            // Promise.all as prefs and channels, a 500 from the integrations list
            // rejected the whole thing: the user's schedule, timezone, quiet hours
            // and channels never rendered, because we could not list their
            // calendars. Degrade to an empty list — the Connected calendars section
            // shows nothing, and everything else on the page works.
            listIntegrations().catch(() => [] as Integration[]),
        ])
            .then(([p, ch, ints]) => {
                // A user who never opted in gets disabled defaults from the API;
                // seed the timezone from the browser so the first save is right
                // without the user hunting through 400+ zones.
                setPrefs({ ...p, timezone: p.timezone || browserTimezone() });
                setChannels(ch);
                setIntegrations(ints);
            })
            .catch(err => toast.error(`Failed to load assistant settings: ${getErrorMessage(err)}`))
            .finally(() => setLoading(false));
    }, []);

    // The OAuth callback redirects back to /profile?tab=assistant with a result
    // flag. Read it once, toast, and STRIP it from the URL — otherwise a refresh
    // re-toasts a connection the user made ten minutes ago.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const connected = params.get('calendar_connected');
        const failed = params.get('calendar_error');
        if (!connected && !failed) return;

        if (connected) {
            toast.success('Google Calendar connected');
        } else if (failed) {
            toast.error(CALENDAR_ERRORS[failed] ?? 'Could not connect that calendar. Try again.');
        }

        params.delete('calendar_connected');
        params.delete('calendar_error');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }, []);

    // Stop the verification poll if the user leaves the tab mid-link.
    useEffect(() => () => stopPolling(), []);

    function stopPolling() {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }

    function startVerificationPoll() {
        stopPolling();
        pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
        pollRef.current = setInterval(async () => {
            if (Date.now() > pollDeadlineRef.current) {
                stopPolling();
                setTgLink(null);
                toast.error('Telegram link timed out — the code expired. Click Connect to get a new one.');
                return;
            }
            try {
                const ch = await listAssistantChannels();
                const verified = ch.find(c => c.kind === 'telegram' && c.verified_at !== null);
                if (verified) {
                    stopPolling();
                    setTgLink(null);
                    setChannels(ch);
                    toast.success('Telegram connected');
                }
            } catch {
                // Deliberate silent catch — the ONE allowed in this file: a
                // transient poll failure just retries in 3s; a persistent
                // outage surfaces as the deadline toast above.
            }
        }, POLL_INTERVAL_MS);
    }

    async function handleConnectTelegram() {
        setLinking(true);
        try {
            const link = await linkTelegram();
            setTgLink(link);
            startVerificationPoll();
        } catch (err) {
            toast.error(`Telegram link failed: ${getErrorMessage(err)}`);
        } finally {
            setLinking(false);
        }
    }

    async function handleConnectGoogle() {
        setConnectingGoogle(true);
        try {
            const url = await getGoogleConsentURL();
            // A FULL-PAGE redirect, not a popup: Google's consent screen refuses
            // to render inside an iframe, and popups get blocked. The callback
            // brings the user back to this exact tab.
            window.location.assign(url);
        } catch (err) {
            toast.error(`Could not start the Google connection: ${getErrorMessage(err)}`);
            setConnectingGoogle(false); // on success the page is navigating away
        }
    }

    async function handleConnectICloud(e: React.FormEvent) {
        e.preventDefault();
        setConnectingICloud(true);
        try {
            const created = await connectICloud(appleId.trim(), appPassword.trim());
            setIntegrations(list => [...list.filter(i => i.provider !== 'icloud_caldav'), created]);
            // Clear the password from component state the moment it is no longer
            // needed. It is already out of our hands (encrypted server-side) and
            // this credential authenticates far more than calendars.
            setAppleId('');
            setAppPassword('');
            toast.success('iCloud Calendar connected');
        } catch (err) {
            // The backend's message is deliberately specific ("use an
            // app-specific password, not your Apple ID password") because that is
            // the single most common failure. Surface it verbatim.
            toast.error(getErrorMessage(err));
        } finally {
            setConnectingICloud(false);
        }
    }

    async function handleDisconnectIntegration(i: Integration) {
        const name = PROVIDER_NAMES[i.provider] ?? i.provider;
        if (!window.confirm(`Disconnect ${name}? Your cached events are deleted and briefings stop mentioning your calendar.`)) return;
        try {
            await deleteIntegration(i.id);
            setIntegrations(list => list.filter(x => x.id !== i.id));
            toast.success(`${name} disconnected`);
        } catch (err) {
            toast.error(`Could not disconnect ${name}: ${getErrorMessage(err)}`);
        }
    }

    // Reconnecting is just connecting again: the backend's Upsert replaces the
    // credentials AND clears status/consecutive_failures, so a successful
    // reconnect is what unlocks a locked-out integration.
    function handleReconnectIntegration(i: Integration) {
        if (i.provider === 'google_calendar') {
            void handleConnectGoogle();
        }
    }

    async function handleEnabledToggle(next: boolean) {
        if (!prefs) return;
        const before = prefs;
        setPrefs({ ...prefs, enabled: next }); // optimistic
        setTogglingEnabled(true);
        try {
            // Send the timezone along: the first enable upserts the prefs row,
            // which must be created with the browser-derived zone, not UTC.
            await updateAssistantPrefs({ enabled: next, timezone: prefs.timezone });
            if (next) {
                // Enabling provisions the email channel server-side — refresh.
                setChannels(await listAssistantChannels());
            }
            toast.success(next ? 'Assistant enabled' : 'Assistant disabled');
        } catch (err) {
            setPrefs(before); // roll back the optimistic flip
            toast.error(`Could not update assistant: ${getErrorMessage(err)}`);
        } finally {
            setTogglingEnabled(false);
        }
    }

    async function handleSave() {
        if (!prefs) return;
        setSaving(true);
        try {
            // Wire contract for the four time slots (FIXED): absent = unchanged,
            // "" = clear to NULL, "HH:MM" = set. Local state stores null for a
            // cleared slot, so serialize null → "" — never send null.
            const echoed = await updateAssistantPrefs({
                timezone: prefs.timezone,
                morning_time: prefs.morning_time ?? '',
                evening_time: prefs.evening_time ?? '',
                urgent_alerts: prefs.urgent_alerts,
                quiet_hours_start: prefs.quiet_hours_start ?? '',
                quiet_hours_end: prefs.quiet_hours_end ?? '',
            });
            // The PATCH echo is the BARE prefs row — it carries NO capabilities
            // key. Spread it over the previous state so the capabilities object
            // fetched by GET survives the save; replacing state wholesale with
            // the echo would set capabilities to undefined and hide the
            // Telegram card (and crash the capability gate).
            setPrefs(p => (p ? { ...p, ...echoed } : p));
            toast.success('Assistant preferences saved');
        } catch (err) {
            toast.error(`Save failed: ${getErrorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    }

    async function handleChannelToggle(ch: AssistantChannel, field: 'receive_briefings' | 'receive_urgent', value: boolean) {
        const before = channels;
        const next = { ...ch, [field]: value };
        setChannels(cs => cs.map(c => (c.id === ch.id ? next : c))); // optimistic
        try {
            // Send both toggles so the PATCH body always carries the channel's
            // full toggle state — no guessing about missing fields server-side.
            await updateAssistantChannel(ch.id, {
                receive_briefings: next.receive_briefings,
                receive_urgent: next.receive_urgent,
            });
        } catch (err) {
            setChannels(before); // roll back
            toast.error(`Could not update channel: ${getErrorMessage(err)}`);
        }
    }

    async function handleChannelDelete(ch: AssistantChannel) {
        if (!window.confirm('Disconnect this channel? The assistant will stop sending to it.')) return;
        try {
            await deleteAssistantChannel(ch.id);
            setChannels(cs => cs.filter(c => c.id !== ch.id));
            toast.success('Channel disconnected');
        } catch (err) {
            toast.error(`Could not disconnect channel: ${getErrorMessage(err)}`);
        }
    }

    async function handlePreview() {
        setPreviewing(true);
        try {
            setPreview(await previewBriefing());
        } catch (err) {
            toast.error(`Preview failed: ${getErrorMessage(err)}`);
        } finally {
            setPreviewing(false);
        }
    }

    if (loading) return <div className="spinner" />;

    if (!prefs) {
        return <p className="form-error">Assistant settings failed to load — refresh the page to retry.</p>;
    }

    const emailChannel = channels.find(c => c.kind === 'email');
    const telegramChannel = channels.find(c => c.kind === 'telegram');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* Header */}
            <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 700 }}>
                    <Sparkles size={18} aria-hidden="true" /> Proactive Assistant
                </h3>
                <p style={{ marginTop: 4, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    Morning and evening briefings of your work, plus urgent overdue alerts, on the channels you choose.
                </p>
            </div>

            {/* Master toggle */}
            <label style={{ ...cardStyle, cursor: 'pointer' }}>
                <input
                    type="checkbox"
                    checked={prefs.enabled}
                    disabled={togglingEnabled}
                    onChange={e => handleEnabledToggle(e.target.checked)}
                />
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Enable proactive assistant</span>
            </label>

            {/* Schedule */}
            <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>Schedule</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <div>
                        <label className="form-label" htmlFor="assistant-timezone" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                            <Globe size={14} aria-hidden="true" /> Timezone
                        </label>
                        <select
                            id="assistant-timezone"
                            className="form-input"
                            value={prefs.timezone}
                            onChange={e => setPrefs(p => (p ? { ...p, timezone: e.target.value } : p))}
                        >
                            {timezoneOptions(prefs.timezone).map(tz => (
                                <option key={tz} value={tz}>{tz}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <TimeField
                            id="assistant-morning"
                            label="Morning briefing"
                            icon={Sunrise}
                            value={prefs.morning_time}
                            onChange={v => setPrefs(p => (p ? { ...p, morning_time: v } : p))}
                        />
                        <TimeField
                            id="assistant-evening"
                            label="Evening briefing"
                            icon={Moon}
                            value={prefs.evening_time}
                            onChange={v => setPrefs(p => (p ? { ...p, evening_time: v } : p))}
                        />
                    </div>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                        Clear a time to skip that briefing. Times are in your selected timezone.
                    </p>
                </div>
            </div>

            {/* Channels */}
            <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>Channels</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {emailChannel ? (
                        <ChannelCard channel={emailChannel} icon={Mail} title="Email" onToggle={handleChannelToggle} onDelete={handleChannelDelete} />
                    ) : (
                        <div style={cardStyle}>
                            <Mail size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Email</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                    Added automatically from your account email when the assistant is enabled.
                                </div>
                            </div>
                        </div>
                    )}
                    {prefs.capabilities.telegram && (telegramChannel && telegramChannel.verified_at !== null ? (
                        <ChannelCard channel={telegramChannel} icon={Send} title="Telegram" onToggle={handleChannelToggle} onDelete={handleChannelDelete} />
                    ) : tgLink ? (
                        <div style={{ ...cardStyle, alignItems: 'flex-start' }}>
                            <Send size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Telegram</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                    Open Telegram and press <strong>Start</strong>. If the button doesn't work, send the
                                    bot this command: <code style={{ userSelect: 'all' }}>/start {tgLink.code}</code>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                                    <a className="btn btn-primary btn-sm" href={tgLink.deep_link} target="_blank" rel="noopener noreferrer">
                                        Open Telegram
                                    </a>
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                        Waiting for confirmation — this page updates automatically.
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={cardStyle}>
                            <Send size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Telegram</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                    Get briefings and urgent alerts as Telegram messages.
                                </div>
                            </div>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleConnectTelegram} disabled={linking}>
                                {linking ? 'Connecting…' : 'Connect Telegram'}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Connected calendars */}
            {(prefs.capabilities.google_calendar || prefs.capabilities.icloud_calendar) && (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>
                        Connected calendars
                    </div>
                    <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                        Your briefings will mention today's meetings and how much free time you have.
                        KaziAtlas reads <strong>event titles and times only</strong> — never attendees,
                        locations, or descriptions.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {integrations.map(i => (
                            <IntegrationCard
                                key={i.id}
                                integration={i}
                                onDisconnect={handleDisconnectIntegration}
                                onReconnect={handleReconnectIntegration}
                            />
                        ))}

                        {prefs.capabilities.google_calendar
                            && !integrations.some(i => i.provider === 'google_calendar') && (
                            <div style={cardStyle}>
                                <Calendar size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Google Calendar</div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                        Read-only access to your events, so briefings know your schedule.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    aria-label="Connect Google Calendar"
                                    onClick={handleConnectGoogle}
                                    disabled={connectingGoogle}
                                >
                                    {connectingGoogle ? 'Redirecting…' : 'Connect'}
                                </button>
                            </div>
                        )}

                        {prefs.capabilities.icloud_calendar
                            && !integrations.some(i => i.provider === 'icloud_caldav') && (
                            <form
                                onSubmit={handleConnectICloud}
                                style={{ ...cardStyle, alignItems: 'flex-start', flexDirection: 'column', gap: 'var(--space-2)' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <Calendar size={18} aria-hidden="true" />
                                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>iCloud Calendar</span>
                                </div>

                                {/* SPEC §9 — the informed-consent bar. An Apple app-specific
                                    password authenticates other iCloud protocols too, not just
                                    calendars. The user must be told this PLAINLY, before they
                                    hand it over, along with how to revoke it. */}
                                <div
                                    style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-muted)',
                                        background: 'var(--color-surface-3)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-sm)',
                                        padding: 'var(--space-2)',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    <strong>Before you continue:</strong> Apple does not offer a
                                    calendar-only login. An app-specific password lets an app sign in
                                    to <strong>more than your calendar</strong> — it authenticates
                                    other iCloud services too. KaziAtlas stores it encrypted, only
                                    ever reads your events, and never logs it. You can{' '}
                                    <strong>revoke it at any time</strong> at appleid.apple.com, which
                                    instantly cuts off this connection.
                                    <div style={{ marginTop: 'var(--space-1)' }}>
                                        <a
                                            href={APPLE_APP_PASSWORD_DOCS}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: 'var(--color-primary)' }}
                                        >
                                            How to create an app-specific password
                                        </a>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', width: '100%' }}>
                                    <div style={{ flex: 1, minWidth: 180 }}>
                                        <label className="form-label" htmlFor="icloud-apple-id">Apple ID</label>
                                        <input
                                            id="icloud-apple-id"
                                            type="email"
                                            className="form-input"
                                            autoComplete="off"
                                            placeholder="you@icloud.com"
                                            value={appleId}
                                            onChange={e => setAppleId(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 180 }}>
                                        <label className="form-label" htmlFor="icloud-app-password">
                                            App-specific password
                                        </label>
                                        <input
                                            id="icloud-app-password"
                                            type="password"
                                            className="form-input"
                                            autoComplete="off"
                                            placeholder="abcd-efgh-ijkl-mnop"
                                            value={appPassword}
                                            onChange={e => setAppPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-secondary btn-sm"
                                    disabled={connectingICloud || !appleId.trim() || !appPassword.trim()}
                                >
                                    {connectingICloud ? 'Connecting…' : 'Connect iCloud'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Urgent alerts + quiet hours */}
            <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>Urgent alerts</div>
                <label style={{ ...cardStyle, cursor: 'pointer', padding: 'var(--space-2) var(--space-3)' }}>
                    <TriangleAlert size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>Alert me when one of my tasks becomes overdue</span>
                    <input
                        type="checkbox"
                        checked={prefs.urgent_alerts}
                        onChange={e => setPrefs(p => (p ? { ...p, urgent_alerts: e.target.checked } : p))}
                    />
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
                    <TimeField
                        id="assistant-quiet-start"
                        label="Quiet hours start"
                        icon={Moon}
                        value={prefs.quiet_hours_start}
                        onChange={v => setPrefs(p => (p ? { ...p, quiet_hours_start: v } : p))}
                    />
                    <TimeField
                        id="assistant-quiet-end"
                        label="Quiet hours end"
                        icon={Sunrise}
                        value={prefs.quiet_hours_end}
                        onChange={v => setPrefs(p => (p ? { ...p, quiet_hours_end: v } : p))}
                    />
                </div>
                <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    Urgent alerts are held during quiet hours and delivered when they end.
                </p>
            </div>

            {/* Save + preview */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save preferences'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handlePreview} disabled={previewing}>
                    <Eye size={14} aria-hidden="true" /> {previewing ? 'Composing…' : 'Preview briefing'}
                </button>
            </div>

            {preview && (
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--font-size-sm)' }}>
                        <strong style={{ flex: 1 }}>{preview.subject}</strong>
                        <span className={`badge ${preview.narrative_source === 'llm' ? 'badge-info' : 'badge-neutral'}`}>
                            {preview.narrative_source === 'llm' ? 'AI narrative' : 'Fallback narrative'}
                        </span>
                        <button type="button" className="btn btn-secondary btn-sm" aria-label="Close preview" onClick={() => setPreview(null)}>
                            <X size={14} aria-hidden="true" />
                        </button>
                    </div>
                    {/* sandbox="" (no permission tokens): scripts, forms and top-navigation
                        in the briefing HTML are all blocked. The HTML is server-composed,
                        but an LLM narrative feeds into it — treat it as untrusted. */}
                    <iframe
                        title="Briefing preview"
                        sandbox=""
                        srcDoc={preview.html}
                        style={{ width: '100%', height: 420, border: 'none', background: '#ffffff' }}
                    />
                </div>
            )}
        </div>
    );
}
